import {
    Song,
    TiktokAudioAsset,
    TiktokAudioStats,
    getSongs,
    getTiktokAudioAssets,
    getTiktokAudioStats,
    ClientMonitoredTiktokAudioRelationship,
    getClientMonitoredTiktokAudioRelationships,
    TiktokAudioCollectionTiktokAudioRelationship,
} from '@round/api';
import { useCallback, useState } from 'react';
import { showNotification } from '../../../../../../helpers';
import useAbortableEffect from '../../../../../../Hooks/useAbortableEffect';

type UseTiktokAudiosParams = Omit<Parameters<typeof getTiktokAudioStats>[0], 'page_size'> & {
    page_size: number;
};

type UseTiktokAudiosReturnValue = {
    tiktokAudioStats: TiktokAudioStats[];
    statsCount: number;
    tiktokAudioAssets: TiktokAudioAsset[];
    linkedSongs: Song[];
    clientMonitoredAudioRelations: ClientMonitoredTiktokAudioRelationship[];
    isLoading: boolean;
    hasError: boolean;
    handleAudioLinked: (audioId: number, song: Song) => void;
    handleAudioAddedToCollection: (relation: TiktokAudioCollectionTiktokAudioRelationship) => void;
    handleAudioRemovedFromCollection: (relation: TiktokAudioCollectionTiktokAudioRelationship) => void;
    handleUpdateClientMonitoredAudioRelation: (relation: ClientMonitoredTiktokAudioRelationship) => void;
};

export const useTiktokAudios = ({
    page,
    page_size,
    search,
    min_video_count,
    max_video_count,
    is_original,
    ordering,
    tiktok_sound_ids,
}: UseTiktokAudiosParams): UseTiktokAudiosReturnValue => {
    const [tiktokAudioStats, setTiktokAudioStats] = useState<TiktokAudioStats[]>([]);
    const [statsCount, setStatsCount] = useState(0);
    const [tiktokAudioAssets, setTiktokAudioAssets] = useState<TiktokAudioAsset[]>([]);
    const [linkedSongs, setLinkedSongs] = useState<Song[]>([]);
    const [clientMonitoredAudioRelations, setClientMonitoredAudioRelations] = useState<
        ClientMonitoredTiktokAudioRelationship[]
    >([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    const fetchTiktokAudioAssets = useCallback(async (...params: Parameters<typeof getTiktokAudioAssets>) => {
        try {
            const assets = await getTiktokAudioAssets(...params);
            setTiktokAudioAssets(assets);
        } catch (e) {
            if (e instanceof Error && e.name === 'AbortError') {
                return;
            }
            showNotification('Could not fetch TikTok audio assets', 'error');
        }
    }, []);

    const fetchLinkedSongs = useCallback(async (...params: Parameters<typeof getSongs>) => {
        try {
            const {
                data: { results },
            } = await getSongs(...params);
            setLinkedSongs(results);
        } catch (e) {
            if (e instanceof Error && e.name === 'AbortError') {
                return;
            }
            showNotification('Could not fetch Linked Songs', 'error');
        }
    }, []);

    const fetchClientMonitoredAudioRelations = useCallback(
        async (monitoredAudioIds: number[], pageSize: number, requestInit?: RequestInit) => {
            if (!monitoredAudioIds.length) {
                return;
            }

            try {
                const response = await getClientMonitoredTiktokAudioRelationships(
                    { monitored_audio_id: monitoredAudioIds.join(), page: 1, page_size: pageSize },
                    requestInit
                );

                if (response.status === 200) {
                    setClientMonitoredAudioRelations(response.data.results);
                }
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }
                showNotification('Failed to load custom names', 'error');
            }
        },
        []
    );

    const fetchAudioStatsData = useCallback(
        async (params: UseTiktokAudiosParams, requestInit: RequestInit) => {
            if (!params.ordering) {
                return;
            }
            setIsLoading(true);
            setHasError(false);
            const pageSize = params.page_size;
            try {
                const {
                    data: { results, count },
                } = await getTiktokAudioStats(params);
                setTiktokAudioStats(results);
                setStatsCount(count);

                const audioIds = results.map((audio) => audio.music_id);
                const monitoredMusicIds = results.map((audio) => audio.monitored_music_id);
                if (audioIds.length) {
                    await Promise.allSettled([
                        fetchTiktokAudioAssets(audioIds, requestInit),
                        fetchLinkedSongs({ tiktok_audios: audioIds.join(',') }, requestInit),
                        fetchClientMonitoredAudioRelations(monitoredMusicIds, pageSize, requestInit),
                    ]);
                }
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }

                showNotification('Could not fetch TikTok audio stats', 'error');
                setHasError(true);
            } finally {
                setIsLoading(false);
            }
        },
        [fetchTiktokAudioAssets, fetchLinkedSongs, fetchClientMonitoredAudioRelations]
    );

    useAbortableEffect(
        (signal) => {
            fetchAudioStatsData(
                { page, page_size, search, min_video_count, max_video_count, is_original, ordering, tiktok_sound_ids },
                { signal }
            );
        },
        [
            fetchAudioStatsData,
            page,
            page_size,
            search,
            min_video_count,
            max_video_count,
            is_original,
            ordering,
            tiktok_sound_ids,
        ]
    );

    const handleAudioAddedToCollection = useCallback((relation: TiktokAudioCollectionTiktokAudioRelationship) => {
        setTiktokAudioStats((stats) =>
            stats.map((s) =>
                s.music_id === relation.music ? { ...s, catalogue_ids: s.catalogue_ids.concat(relation.catalogue) } : s
            )
        );
    }, []);

    const handleAudioRemovedFromCollection = useCallback((relation: TiktokAudioCollectionTiktokAudioRelationship) => {
        setTiktokAudioStats((stats) =>
            stats.map((s) =>
                s.music_id === relation.music
                    ? { ...s, catalogue_ids: s.catalogue_ids.filter((id) => id !== relation.catalogue) }
                    : s
            )
        );
    }, []);

    const handleUpdateClientMonitoredAudioRelation = useCallback(
        (updatedRelation: ClientMonitoredTiktokAudioRelationship) => {
            setClientMonitoredAudioRelations((relations) =>
                relations.map((rel) => (rel.id === updatedRelation.id ? updatedRelation : rel))
            );
        },
        []
    );

    const handleAudioLinked = useCallback((audioId: number, song: Song) => {
        setLinkedSongs((songs) => songs.concat({ ...song, tiktok_audios: song.tiktok_audios.concat(audioId) }));
    }, []);

    return {
        tiktokAudioStats,
        statsCount,
        tiktokAudioAssets,
        linkedSongs,
        clientMonitoredAudioRelations,
        isLoading,
        hasError,
        handleAudioLinked,
        handleAudioAddedToCollection,
        handleAudioRemovedFromCollection,
        handleUpdateClientMonitoredAudioRelation,
    };
};
